import { Icon } from '@garnet/ui';
import { APPS } from './apps.js';

/**
 * Garnet platform landing page — the single entry point that links to every frontend.
 *
 * The five apps are served (locally or via docker-compose) on their canonical Vite ports
 * (5171–5175); this page is itself served on 5170. The URL of each app can be overridden
 * at build time with a VITE_* env var (e.g. `VITE_CONSOLE_URL=https://console.garnet.example`)
 * — defaults match the local-dev / docker-compose layout so it works out of the box.
 */

/** Read a Vite env var with a fallback; safe when vite/client types aren't referenced. */
function envUrl(envVar: string, fallback: string): string {
  const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
  const v = env?.[envVar];
  return v && v.length > 0 ? v : fallback;
}

export function App() {
  return (
    <div className="landing">
      <header className="hero">
        <div className="hero-inner">
          <div className="brand">
            <span className="gem" aria-hidden="true" />
            <span className="wordmark">Garnet</span>
          </div>
          <p className="kicker">procurement diligence · regulatory rule store</p>
          <h1 className="display">One platform, every diligence surface.</h1>
          <p className="lead">
            Choose where you want to go. Each app opens in its own window — they are independent
            products sharing one verified, audit-ready record.
          </p>
        </div>
      </header>

      <main className="grid" aria-label="Garnet applications">
        {APPS.map((app) => {
          const url = envUrl(app.envVar, app.defaultUrl);
          return (
            <a
              key={app.key}
              className={`card accent-${app.accent}`}
              href={url}
              target="_blank"
              rel="noreferrer noopener"
            >
              <div className="card-head">
                <span className="swatch" aria-hidden="true">
                  <Icon name={app.icon} size={20} />
                </span>
                <span className="audience mono">{app.audience}</span>
              </div>
              <h2 className="card-title">{app.name}</h2>
              <p className="card-desc">{app.description}</p>
              <div className="card-foot">
                <span className="cta">
                  Open {app.name}
                  <Icon name="arrow" size={14} />
                </span>
                <span className="url mono">{url.replace(/^https?:\/\//, '')}</span>
              </div>
            </a>
          );
        })}
      </main>

      <footer className="foot">
        <div className="foot-inner">
          <span className="mono">
            Garnet · build deterministic, cited, human-in-the-loop diligence.
          </span>
          <span className="mono dim">local · cell: eu · no sign-in required for launch</span>
        </div>
      </footer>
    </div>
  );
}
